/**
 * Hyperlocal Links Block
 *
 * Renders the property's `hyperlocal_queries` collection as a continuously
 * scrolling marquee of pill-shaped links. Each query is paired 1:1 with an
 * expanded hyperlocal page (see `expandTemplatePages` for `template:
 * 'hyperlocal'`), so the link text comes from the query and the URL is built
 * from a `{{page_item_url}}` template that mirrors the page's own path
 * pattern (default `/featuring/{{page_item_url}}`).
 *
 * The block exists for SEO: every hyperlocal page needs at least one
 * crawlable inbound link or it is orphaned. Only the FIRST copy of each
 * marquee row is real content — the extra copies that make the loop seamless
 * are `aria-hidden` and their links carry `tabindex="-1"`, so crawlers and
 * assistive tech see each link exactly once.
 *
 * Motion is pure CSS (SSG-safe, no hydration needed): rows scroll in
 * alternating directions at slightly different speeds, pause on hover and on
 * keyboard focus, and collapse to a static centered pill cloud under
 * `prefers-reduced-motion`.
 */

import type { CSSProperties } from 'react';
import type { BlockDefinition, BlockProps } from '../../types';
import type { ComponentContentSchema } from '@vibesites/runtime';
import type { HyperlocalQuery } from '@vibesites/schema';
import { useBlockToken, EditableBlockToken, useProperty } from '@vibesites/runtime/react';
import { defineComponentRequirements, collections } from '@vibesites/tokens';

// ============================================================================
// Helpers
// ============================================================================

const LABEL_FIELD_FALLBACKS: Array<keyof HyperlocalQuery> = [
  'h1_title',
  'search_query',
  'h2_subtitle',
  'page_item_url',
];

const DEFAULT_URL_PATTERN = '/featuring/{{page_item_url}}';

/**
 * Resolve a link label by trying the user-selected field first, then a
 * fixed fallback chain so we never render a blank link.
 */
function resolveLabel(
  query: HyperlocalQuery,
  preferredField: keyof HyperlocalQuery,
): string {
  const fields = [preferredField, ...LABEL_FIELD_FALLBACKS.filter(f => f !== preferredField)];
  for (const f of fields) {
    const v = query[f];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

/**
 * Substitute `{{page_item_url}}` (and a few other useful tokens) in the URL
 * template against a single query. Tokens missing from the query collapse
 * to empty strings.
 */
function buildHref(template: string, query: HyperlocalQuery): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, key) => {
    const v = (query as Record<string, unknown>)[key];
    return typeof v === 'string' ? v : '';
  });
}

function clampNum(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

/**
 * Split items into `rowCount` contiguous slices whose sizes differ by at
 * most one. Contiguous (rather than round-robin) so category-sorted input
 * keeps related links together within a row.
 */
function splitRows<T>(items: T[], rowCount: number): T[][] {
  const rows: T[][] = [];
  const base = Math.floor(items.length / rowCount);
  let extra = items.length % rowCount;
  let idx = 0;
  for (let r = 0; r < rowCount; r++) {
    const take = base + (extra > 0 ? 1 : 0);
    if (extra > 0) extra--;
    if (take === 0) continue;
    rows.push(items.slice(idx, idx + take));
    idx += take;
  }
  return rows;
}

// ----------------------------------------------------------------------------
// Marquee geometry
//
// The loop is pure CSS: a track holds N identical copies of the row and
// animates `translateX(-100/N %)`. There is no client JS to measure real
// widths, so N and the animation duration are derived from an estimate of
// the rendered row width (average glyph width × label length + chip chrome).
// The estimate only needs to be roughly right: N just has to make the track
// wider than any realistic viewport, and duration scales speed to width so
// long rows don't whip past.
// ----------------------------------------------------------------------------

/** Average glyph width in px at the chip's 0.875rem font size. */
const AVG_CHAR_PX = 7.2;
/** Horizontal padding + icon + gap + borders of one chip, in px. */
const CHIP_CHROME_PX = 56;
/** Flex gap between chips (must match --vibe-hlm-gap), in px. */
const CHIP_GAP_PX = 12;
/** Widest viewport the loop must cover without a visible gap. */
const VIEWPORT_BUDGET_PX = 2600;
/** Per-row speed multipliers so parallel rows drift organically. */
const SPEED_VARIANCE = [1, 1.18, 0.92, 1.3];

function estimateGroupWidthPx(
  slice: HyperlocalQuery[],
  labelKey: keyof HyperlocalQuery,
): number {
  return Math.max(
    1,
    slice.reduce(
      (w, q) => w + CHIP_CHROME_PX + resolveLabel(q, labelKey).length * AVG_CHAR_PX + CHIP_GAP_PX,
      0,
    ),
  );
}

// ============================================================================
// Marquee CSS (inline <style> so it ships in static SSG output)
// ============================================================================

const MARQUEE_CSS = `
.vibe-hlm__rows{display:flex;flex-direction:column;gap:0.8rem}
.vibe-hlm__viewport{position:relative;overflow:hidden;-webkit-mask-image:linear-gradient(to right,transparent,#000 var(--vibe-hlm-fade),#000 calc(100% - var(--vibe-hlm-fade)),transparent);mask-image:linear-gradient(to right,transparent,#000 var(--vibe-hlm-fade),#000 calc(100% - var(--vibe-hlm-fade)),transparent)}
.vibe-hlm__viewport:focus-within{-webkit-mask-image:none;mask-image:none}
.vibe-hlm__track{display:flex;width:max-content;will-change:transform;animation:vibe-hlm-scroll var(--vibe-hlm-dur,60s) linear infinite}
.vibe-hlm__track--reverse{animation-direction:reverse}
.vibe-hlm__viewport:hover .vibe-hlm__track,
.vibe-hlm__viewport:focus-within .vibe-hlm__track{animation-play-state:paused}
@keyframes vibe-hlm-scroll{to{transform:translateX(var(--vibe-hlm-shift,-50%))}}
.vibe-hlm__group{display:flex;align-items:center;gap:var(--vibe-hlm-gap);list-style:none;margin:0;padding:0 var(--vibe-hlm-gap) 0 0}
.vibe-hlm__chip{display:inline-flex;align-items:center;gap:0.45rem;padding:0.5rem 1.05rem;border-radius:999px;font-size:0.875rem;line-height:1.25;white-space:nowrap;text-decoration:none;color:var(--vibe-color-text,#333333);border:1px solid rgba(127,127,127,0.28);border-color:color-mix(in srgb,var(--vibe-color-text,#333333) 16%,transparent);background:transparent;background:color-mix(in srgb,var(--vibe-color-text,#333333) 4%,transparent);transition:color 0.18s ease,border-color 0.18s ease,background-color 0.18s ease,transform 0.18s ease}
.vibe-hlm__chip:hover{color:var(--vibe-hlm-accent);border-color:var(--vibe-hlm-accent);border-color:color-mix(in srgb,var(--vibe-hlm-accent) 55%,transparent);background:transparent;background:color-mix(in srgb,var(--vibe-hlm-accent) 8%,transparent);transform:translateY(-1px)}
.vibe-hlm__chip:focus-visible{outline:2px solid var(--vibe-hlm-accent);outline-offset:2px}
.vibe-hlm__chip svg{flex:none;opacity:0.45;transition:opacity 0.18s ease,transform 0.18s ease}
.vibe-hlm__chip:hover svg{opacity:1;transform:translate(1px,-1px)}
@media (prefers-reduced-motion:reduce){
.vibe-hlm__track{animation:none;width:auto;flex-wrap:wrap;justify-content:center}
.vibe-hlm__group[aria-hidden="true"]{display:none}
.vibe-hlm__group{flex-wrap:wrap;justify-content:center;row-gap:var(--vibe-hlm-gap);padding-right:0}
.vibe-hlm__viewport{-webkit-mask-image:none;mask-image:none}
.vibe-hlm__chip:hover{transform:none}
.vibe-hlm__chip svg{transition:none}
}
`;

function ArrowUpRightIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M7 17 17 7" />
      <path d="M8 7h9v9" />
    </svg>
  );
}

// ============================================================================
// Component
// ============================================================================

function HyperlocalLinksComponent({ sectionId: _sectionId }: BlockProps) {
  const property = useProperty();
  const queries = (property?.hyperlocal_queries as HyperlocalQuery[] | null | undefined) || [];

  const title = useBlockToken('title', { defaultValue: 'Explore the Area' });
  const subtitle = useBlockToken('subtitle', { defaultValue: '' });
  const showHeader = useBlockToken('show_header', { defaultValue: 'true' });
  const rowsStr = useBlockToken('rows', { defaultValue: '2' });
  const speedStr = useBlockToken('speed', { defaultValue: '36' });
  const urlPattern = useBlockToken('url_pattern', { defaultValue: DEFAULT_URL_PATTERN });
  const labelField = useBlockToken('label_field', { defaultValue: 'h1_title' });
  const limitStr = useBlockToken('limit', { defaultValue: '0' });
  const sortBy = useBlockToken('sort_by', { defaultValue: 'category' });

  const rowCount = clampNum(parseInt(rowsStr || '2', 10) || 2, 1, 4);
  const pxPerSecond = clampNum(parseInt(speedStr || '36', 10) || 36, 12, 160);
  const limit = Math.max(0, parseInt(limitStr || '0', 10) || 0);
  const headerVisible = showHeader === 'true';
  const labelKey = (labelField || 'h1_title') as keyof HyperlocalQuery;

  // Filter to items that can produce a real link, then sort so adjacent
  // chips share a category (the rows are contiguous slices of this order).
  const items = queries
    .filter((q): q is HyperlocalQuery => !!q && typeof q.page_item_url === 'string' && !!q.page_item_url.trim())
    .slice();

  if (sortBy === 'category') {
    items.sort((a, b) => (a.category || '').localeCompare(b.category || '') || resolveLabel(a, labelKey).localeCompare(resolveLabel(b, labelKey)));
  } else if (sortBy === 'label') {
    items.sort((a, b) => resolveLabel(a, labelKey).localeCompare(resolveLabel(b, labelKey)));
  }

  const visibleItems = limit > 0 ? items.slice(0, limit) : items;

  // Theme-driven defaults — every color/font binds through a CSS variable so
  // the host theme's design tokens (or per-instance design overrides set in
  // the editor) flow through automatically. Falls back to neutral defaults
  // only when the variable is unset.
  const sectionStyle = {
    background: 'var(--vibe-color-background, #ffffff)',
    color: 'var(--vibe-color-text, #333333)',
    fontFamily: 'var(--vibe-font-body)',
    '--vibe-hlm-accent': 'var(--vibe-color-hyperlink, var(--vibe-color-primary, #4f46e5))',
    '--vibe-hlm-gap': '0.75rem',
    '--vibe-hlm-fade': 'clamp(1.5rem, 8vw, 5rem)',
  } as CSSProperties;

  // Empty state — kept lightweight so the editor still shows the block frame
  // and can collect inline-edit overrides for the title/subtitle.
  if (visibleItems.length === 0) {
    return (
      <section className="py-12 px-6 text-center" style={sectionStyle}>
        <EditableBlockToken
          localName="title"
          label="Section Title"
          as="h2"
          className="text-lg font-medium m-0 mb-2"
          style={{ color: 'var(--vibe-color-text)', fontFamily: 'var(--vibe-font-heading)' }}
        >
          {title || 'Hyperlocal Pages'}
        </EditableBlockToken>
        <p className="text-sm m-0" style={{ color: 'var(--vibe-color-text-muted)' }}>
          No hyperlocal queries are configured for this property yet.
        </p>
      </section>
    );
  }

  const rowSlices = splitRows(visibleItems, rowCount);

  return (
    <section className="vibe-hlm py-14 md:py-20 overflow-hidden" style={sectionStyle}>
      <style dangerouslySetInnerHTML={{ __html: MARQUEE_CSS }} />

      {headerVisible && (
        <div className="mb-8 md:mb-10 px-4 md:px-6 text-center">
          <EditableBlockToken
            localName="title"
            label="Section Title"
            as="h2"
            className="text-2xl md:text-3xl font-bold tracking-tight m-0"
            style={{ color: 'var(--vibe-color-text)', fontFamily: 'var(--vibe-font-heading)' }}
          >
            {title}
          </EditableBlockToken>
          {subtitle && (
            <EditableBlockToken
              localName="subtitle"
              label="Section Subtitle"
              as="p"
              className="text-sm m-0 mt-2"
              style={{ color: 'var(--vibe-color-text-muted)' }}
            >
              {subtitle}
            </EditableBlockToken>
          )}
        </div>
      )}

      <div className="vibe-hlm__rows">
        {rowSlices.map((slice, r) => {
          const groupWidth = estimateGroupWidthPx(slice, labelKey);
          // Enough copies that the track always outruns the viewport, +1 so
          // the wrap point is never on screen.
          const copies = clampNum(Math.ceil(VIEWPORT_BUDGET_PX / groupWidth) + 1, 2, 20);
          // Duration scales with row width so every row moves at roughly the
          // configured px/s, with a per-row variance so rows drift apart.
          const duration = clampNum(
            Math.round((groupWidth / pxPerSecond) * SPEED_VARIANCE[r % SPEED_VARIANCE.length]),
            18,
            240,
          );
          const trackStyle = {
            '--vibe-hlm-dur': `${duration}s`,
            '--vibe-hlm-shift': `-${(100 / copies).toFixed(4)}%`,
          } as CSSProperties;

          return (
            <div key={r} className="vibe-hlm__viewport">
              <div
                className={`vibe-hlm__track${r % 2 === 1 ? ' vibe-hlm__track--reverse' : ''}`}
                style={trackStyle}
              >
                {Array.from({ length: copies }, (_, copy) => (
                  // Copy 0 is the real, crawlable, focusable list; the rest
                  // only exist to make the CSS loop seamless.
                  <ul key={copy} className="vibe-hlm__group" aria-hidden={copy > 0 || undefined}>
                    {slice.map((q, i) => {
                      const href = buildHref(urlPattern || DEFAULT_URL_PATTERN, q);
                      const label = resolveLabel(q, labelKey);
                      return (
                        <li key={`${q.page_item_url}-${i}`}>
                          <a
                            href={href}
                            className="vibe-hlm__chip"
                            tabIndex={copy > 0 ? -1 : undefined}
                          >
                            <span>{label}</span>
                            <ArrowUpRightIcon />
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

const contentSchema: ComponentContentSchema = {
  slot: 'HyperlocalLinks',
  displayName: 'Hyperlocal Links',
  tokens: {
    title: {
      label: 'Section Title',
      type: 'text',
      defaultValue: 'Explore the Area',
      description: 'Headline above the marquee.',
    },
    subtitle: {
      label: 'Section Subtitle',
      type: 'text',
      defaultValue: '',
      description: 'Optional smaller line beneath the title.',
    },
    show_header: {
      label: 'Show Header',
      type: 'text',
      defaultValue: 'true',
      description: 'Set to "false" to hide the title and subtitle.',
    },
    rows: {
      label: 'Rows',
      type: 'text',
      defaultValue: '2',
      description: 'Number of scrolling rows (1-4). Adjacent rows scroll in opposite directions.',
    },
    speed: {
      label: 'Scroll Speed',
      type: 'text',
      defaultValue: '36',
      description: 'Marquee speed in pixels per second (12-160). Lower is calmer.',
    },
    url_pattern: {
      label: 'URL Pattern',
      type: 'text',
      defaultValue: DEFAULT_URL_PATTERN,
      description:
        'Path template for each link. Supports {{page_item_url}}, {{search_query}}, {{category}}, etc. — any field on a hyperlocal query.',
    },
    label_field: {
      label: 'Label Field',
      type: 'text',
      defaultValue: 'h1_title',
      description:
        'Which hyperlocal query field to use as the link text. Falls back to h1_title → search_query → h2_subtitle → page_item_url when empty.',
    },
    limit: {
      label: 'Max Items',
      type: 'text',
      defaultValue: '0',
      description: 'Maximum number of links to show. 0 means show all.',
    },
    sort_by: {
      label: 'Sort By',
      type: 'text',
      defaultValue: 'category',
      description:
        'How to order links: "category" (keeps related links next to each other in a row), "label" (alphabetical), or "none" (data order).',
    },
  },
};

// ============================================================================
// Block Definition
// ============================================================================

export const hyperlocalLinksBlock: BlockDefinition = {
  id: 'hyperlocal-links',
  name: 'Hyperlocal Links',
  description:
    "Scrolling marquee of pill links to every page produced by the property's hyperlocal queries. Keeps hyperlocal pages crawlable (non-orphaned) while reading as a designed section rather than an SEO link dump.",
  category: 'Navigation',
  component: HyperlocalLinksComponent,
  contentSchema,
  requirements: defineComponentRequirements('HyperlocalLinks', {
    designTokens: [
      'color.background',
      'color.text',
      'color.textMuted',
      'color.hyperlink',
      'color.primary',
      'font.body',
      'font.heading',
    ],
    dataTokens: [
      collections.optional(
        'hyperlocal_queries',
        'Per-property hyperlocal SEO queries; each item with a page_item_url renders one link.',
      ),
    ],
  }),
  tags: [
    'hyperlocal',
    'links',
    'seo',
    'navigation',
    'marquee',
    'footer',
    'sitemap',
    'queries',
  ],
  defaultGridSpan: 12,
};
